import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Zap, Loader2, Linkedin, Facebook, Twitter, Instagram, CheckCircle2,
  MessageSquare, CalendarDays, ShieldCheck, Eye,
} from "lucide-react";

const PLAT_ICON = { twitter: Twitter, instagram: Instagram, linkedin: Linkedin, facebook: Facebook };
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

function PostReview({ token, post, accent, defaultName }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(post.reviewer_comments || []);
  const [approved, setApproved] = useState(post.reviewer_approved);
  const [name, setName] = useState(defaultName || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const PIcon = PLAT_ICON[post.platform] || Facebook;
  const imgUrl = post.image_path ? `${API}/media/${post.image_path}` : null;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/review/${token}/post/${post.id}/approve`, { author_name: name, decision: "approve" });
      setApproved(true);
      toast.success("Post approved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };
  const sendNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/review/${token}/post/${post.id}/comment`, { author_name: name, note });
      setComments((c) => [...c, r.data.comment]);
      setNote("");
      toast.success("Feedback sent to the team");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`review-post-${post.id}`}>
      <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-b border-border">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground capitalize"><PIcon className="h-3.5 w-3.5" /> {post.platform}</span>
        <span className="text-[11px] text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {fmtDate(post.scheduled_at)}</span>
      </div>
      {imgUrl ? (
        <div className="relative aspect-[1200/630] bg-secondary/40">
          <img src={imgUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      ) : (
        <div className="relative aspect-[1200/630] flex items-center justify-center p-4" style={{ background: `radial-gradient(130% 130% at 80% -10%, ${accent} 0%, #16172e 48%, #0b0b16 100%)` }}>
          <div className="font-display text-base font-extrabold text-white text-center">{post.headline}</div>
        </div>
      )}
      <div className="p-3.5 flex flex-col gap-2 flex-1">
        <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap flex-1">{post.post_text}</p>
        {post.hashtags?.length > 0 && <div className="flex flex-wrap gap-1">{post.hashtags.map((h) => <span key={h} className="text-[11px] text-primary/80">{h}</span>)}</div>}
        <button onClick={() => setShowPreview((v) => !v)} data-testid={`review-preview-toggle-${post.id}`}
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary w-fit">
          <Eye className="h-3 w-3" /> {showPreview ? "Hide preview" : "Preview exactly what will publish"}
        </button>
        {showPreview && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid={`review-preview-${post.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-primary mb-1.5">
              <PIcon className="h-3 w-3" /> {post.platform} · live format
            </div>
            <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-relaxed" data-testid={`review-preview-content-${post.id}`}>
              {post.preview_content || post.post_text}
            </p>
          </div>
        )}
        {comments.length > 0 && (
          <div className="space-y-1.5 mt-1" data-testid={`review-comments-${post.id}`}>
            {comments.map((c, i) => (
              <div key={i} className="text-[11px] rounded-md bg-secondary/40 px-2 py-1.5"><span className="font-medium text-foreground/90">{c.author_name}: </span><span className="text-muted-foreground">{c.note}</span></div>
            ))}
          </div>
        )}
        <div className="mt-1 border-t border-border pt-2">
          {approved ? (
            <div className="flex items-center gap-1.5 text-xs text-emerald-400 font-medium" data-testid={`review-approved-${post.id}`}><CheckCircle2 className="h-4 w-4" /> Approved</div>
          ) : open ? (
            <div className="space-y-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-8 text-xs bg-secondary/40" data-testid={`review-name-${post.id}`} />
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Change request for this post…" className="min-h-[56px] text-xs resize-none bg-secondary/40" data-testid={`review-note-${post.id}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={sendNote} disabled={busy || !note.trim()} variant="outline" className="h-7 text-xs flex-1 border-border" data-testid={`review-send-${post.id}`}>Send feedback</Button>
                <Button size="sm" onClick={approve} disabled={busy} className="h-7 text-xs flex-1 text-white" style={{ background: accent }} data-testid={`review-approve-${post.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Approve</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary" data-testid={`review-feedback-toggle-${post.id}`}><MessageSquare className="h-3 w-3" /> Feedback</button>
              <button onClick={approve} disabled={busy} className="flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 ml-auto" data-testid={`review-quick-approve-${post.id}`}><CheckCircle2 className="h-3 w-3" /> Approve</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientReview() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [approvingAll, setApprovingAll] = useState(false);
  const [name, setName] = useState("");

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const r = await api.post(`/review/${token}/approve-all`, { author_name: name });
      toast.success(`Approved all ${r.data.approved} posts — thank you!`);
      api.get(`/review/${token}`).then((res) => setData(res.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve all"); }
    finally { setApprovingAll(false); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/review/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "This review link is invalid or was revoked."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (err) return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 text-center">
      <div><div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4"><ShieldCheck className="h-7 w-7 text-muted-foreground" /></div>
      <h1 className="text-xl font-display font-bold">Link unavailable</h1><p className="text-muted-foreground mt-2 max-w-sm">{err}</p></div>
    </div>
  );

  const accent = data.brand_accent || "#6366F1";
  const posts = data.posts || [];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="client-review">
      <header className="border-b border-border sticky top-0 z-10 bg-background/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {data.brand_logo_url ? (
            <img src={data.brand_logo_url} alt="" className="h-10 w-10 rounded-lg object-contain bg-white/90 shrink-0" />
          ) : (
            <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0 text-white font-display font-extrabold" style={{ background: accent }}>{(data.brand_name || "B")[0].toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <div className="text-base font-display font-bold leading-tight truncate" data-testid="review-brand">{data.brand_name}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><CalendarDays className="h-3 w-3" /> {posts.length} upcoming post{posts.length !== 1 ? "s" : ""} to review</div>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: accent }}>Content review</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1.5">Review {data.brand_name}'s upcoming posts</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">Approve each post or leave a quick change request — no account or login needed.</p>
          </div>
          {posts.length > 0 && posts.some((p) => !p.reviewer_approved) && (
            <div className="flex items-center gap-2 shrink-0">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="h-10 w-36 text-sm bg-secondary/40" data-testid="review-approveall-name" />
              <Button onClick={approveAll} disabled={approvingAll} className="text-white h-10 shrink-0" style={{ background: accent }} data-testid="review-approve-all">
                {approvingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Approve all {posts.length}
              </Button>
            </div>
          )}
        </div>
        {posts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card/40 py-16 text-center text-muted-foreground" data-testid="review-empty">Nothing queued for review right now. Check back soon.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => <PostReview key={p.id} token={token} post={p} accent={accent} defaultName="" />)}
          </div>
        )}
        <div className="mt-10 flex items-center gap-2 text-xs text-muted-foreground"><Zap className="h-3.5 w-3.5 text-primary" /> Powered by AutoGridSocial</div>
      </main>
    </div>
  );
}
